
import { DesignAction } from '@/store/Design/DesignRoot';
import { Component, Inject, Prop, Ref, Vue } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';

// Services
import DesigningService from '@/ship/Services/DesigningService';

// Utils
import { getCurrentDateString, getWorkDateAfterDays } from '@/utils/Helper';
import { hasPermission } from '@/utils/Permissions';
import { clone, get, intersection } from 'lodash';

// Components
import VButton from '@/components/VButton/VButton.vue';
import VDatePicker from '@/components/VDatePicker.vue';
import VFormField from '@/components/VFormField.vue';
import VInput from '@/components/VInput.vue';
import VModalGeneral from '@/components/VModal/VModalGeneral.vue';
import VSelect from '@/components/VSelect.vue';
import VSpinner from '@/components/VSpinner.vue';
import ModalEnum from '@/ship/Enums/ModalEnum';
import PermissionsEnum from '@/ship/Enums/PermissionsEnum';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

// Interfaces
import { Design, DesignCreateModel, DesignUpdateModel } from '@/ship/Models/Design';
import IModalEvent from '@/ship/Models/IModalEvent';
import Observer from '@/ship/Models/IObserver';
import Organisation from '@/ship/Models/Organisation';
import Project from '@/ship/Models/Project';
import User from '@/ship/Models/User';
import { TCloudService } from '@/utils/TCloudService';
import { TranslateResult } from 'vue-i18n';

// Enums
import DesignStageEnum from '@/ship/Enums/DesignStageEnum';
import DesignStatusEnum from '@/ship/Enums/DesignStatusEnum';

interface ModalMode {
    mode: DesignUpdateModalMode;
    title: string | TranslateResult;
    description?: string | TranslateResult;
    secondaryBtnTitle?: string | TranslateResult;
    submitBtnTitle: string | TranslateResult;
    emit?: string;
    requireDate?: boolean;
    defaultDate?: string;
    status?: DesignStatusEnum;
}

export enum DesignUpdateModalMode {
    Default,
    TaskCreation,
    SendingForRevision,
    OnCreate,
    BackToWork,
    BackToWorkFromRequest,
    BackToRework,
}

@Component({
    components: {
        ValidationObserver,
        ValidationProvider,
        VButton,
        VDatePicker,
        VFormField,
        VInput,
        VModalGeneral,
        VSelect,
        VSpinner,
    },
})
export default class VDesignDataUpdateModal extends Vue {
    @Action GET_ALL_PROJECTS!: () => Promise<Project[]>;

    @DesignAction CREATE_DESIGNS!: (designs: DesignCreateModel[]) => Promise<Design[]>;
    @DesignAction UPDATE_DESIGN!: (params: { designId: number; model: DesignUpdateModel }) => Promise<Design>;

    @State user!: User;
    @State projects!: Project[] | null;

    @Prop({ type: String, default: 'designUpdateModal' })
    public name!: string;

    @Ref() public observer!: Observer;
    @Ref() public modal!: VModalGeneral;

    @Inject() readonly itemType!: string;

    public hasValidationAttempt = false;
    public updatingInProcess = false;

    public designs: Design[] | null = null;
    public editModel: DesignUpdateModel = { generalDesigners: [] };

    getCurrentDateString = getCurrentDateString;

    stages: Array<{ name: string; id: number }> = [];

    modes: ModalMode[] = [];

    public currentMode: ModalMode = {
        mode: DesignUpdateModalMode.Default,
        title: this.$t(`page.designing.modal.planSetData`),
        secondaryBtnTitle: this.$t('page.designing.modal.abort'),
        submitBtnTitle: this.$t('page.designing.modal.save'),
        emit: 'updated',
        requireDate: false,
    };

    public customerExecutors: User[] = [];
    public customerRepresentative: User[] = [];
    public generalDesignersExecutors: User[] = [];
    public generalDesignersRepresentative: User[] = [];
    public generalDesignersManagers: User[] = [];
    public customerManagers: User[] = [];

    public formattedFiles: DesignCreateModel[] = [];

    get allDesignStages() {
        return this.convertEnumToList('page.designing.stages', DesignStageEnum);
    }

    get isModel() {
        return this.itemType === 'model';
    }

    get generalDesigners(): Organisation[] {
        return get(this.currentProject, 'generalDesigners.data') ?? [];
    }

    get currentProject() {
        return this.projects?.find(({ id }) => id === this.editModel.projectId);
    }

    get hasPermissionChange() {
        return hasPermission(PermissionsEnum.DesignsChange);
    }

    get isDisabledChangeExecutors() {
        return !(
            (hasPermission(PermissionsEnum.DesignsChangeExecutors) || this.hasPermissionChange) &&
            this.editModel.projectId
        );
    }

    get isDisabled() {
        return !(this.editModel.projectId && this.hasPermissionChange);
    }

    get isOnChecking() {
        return this.currentMode.status === DesignStatusEnum.checking;
    }

    get isBackToWorkMode() {
        return this.currentMode.mode === DesignUpdateModalMode.BackToWork;
    }

    convertEnumToList(base: string, enumObject: object) {
        const entries = Object.entries(enumObject);
        const list = entries
            .filter(([key, _]) => this.$te(`${base}.${key}`))
            .map(([key, value]) => {
                const name = this.$t(`${base}.${key}`).toString();
                const id = +value;
                return { name, id };
            });
        return list;
    }

    public async currentProjectChangeHandler(validate?: any) {
        if (validate) this.$nextTick(() => validate());

        if (this.currentProject) {
            const executors = await DesigningService.getAllTaskExecutors(this.currentProject.id);

            this.customerExecutors = executors.customerExecutors.data;
            this.customerRepresentative = executors.customerRepresentative.data;
            this.customerManagers = executors.customerManagers.data;
            this.generalDesignersExecutors = executors.generalDesignersExecutors.data;
            this.generalDesignersRepresentative = executors.generalDesignersRepresentative.data;
            this.generalDesignersManagers = executors.generalDesignersManagers.data;

            this.actualizeEditModel();
        } else {
            this.editModel.inspectors =
                this.editModel.designers =
                this.customerExecutors =
                this.editModel.authors =
                this.generalDesignersExecutors =
                this.editModel.executors =
                this.customerRepresentative =
                this.generalDesignersRepresentative =
                this.customerManagers =
                this.generalDesignersManagers =
                    [];
        }
    }

    public actualizeEditModel() {
        this.editModel.authors = intersection(
            this.editModel.authors,
            this.customerExecutors.map(({ id }) => id),
        );
        this.editModel.executors = intersection(
            this.editModel.executors,
            this.generalDesignersExecutors.map(({ id }) => id),
        );
        this.editModel.generalDesigners = intersection(
            this.editModel.generalDesigners,
            this.generalDesigners.map(({ id }) => id),
        );
        this.editModel.inspectors = intersection(
            this.editModel.inspectors,
            this.customerRepresentative.map(({ id }) => id),
        );
        this.editModel.designers = intersection(
            this.editModel.designers,
            this.generalDesignersRepresentative.map(({ id }) => id),
        );
        this.editModel.customerManagers = intersection(
            this.editModel.customerManagers,
            this.customerManagers.map(({ id }) => id),
        );
        this.editModel.generalDesignersManagers = intersection(
            this.editModel.generalDesignersManagers,
            this.generalDesignersManagers.map(({ id }) => id),
        );
    }

    isLoading = true;

    public async showModal(
        designs: Design[],
        mode: DesignUpdateModalMode = DesignUpdateModalMode.Default,
        formattedFiles?: DesignCreateModel[],
    ) {
        this.setMode(mode);
        this.formattedFiles = formattedFiles ?? [];
        this.designs = designs;
        this.resetEditModel();

        this.modal.showModal();
        await this.GET_ALL_PROJECTS()
            .then(async () => {
                await this.initEditModel();
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    public setMode(value: DesignUpdateModalMode) {
        const mode = this.modes.find(({ mode }) => mode === value) ?? this.modes[DesignUpdateModalMode.Default];
        this.currentMode = clone(mode);
    }

    public async updateDesigns() {
        this.hasValidationAttempt = true;

        const valid = await this.observer.validate();

        if (!valid) return;

        try {
            this.updatingInProcess = true;

            if (this.currentMode.mode === DesignUpdateModalMode.OnCreate) {
                this.designs = await this.CREATE_DESIGNS(this.formattedFiles);
            }

            if (!this.designs?.length) return;

            this.editModel = {
                ...this.editModel,
                generalContractorId: this.currentProject?.generalContractorId,
            };

            const promises = this.designs.map(
                async ({ id }) =>
                    await this.UPDATE_DESIGN({
                        designId: id!,
                        model: { ...this.editModel },
                    }),
            );
            await Promise.all(promises);

            if (this.designs[0]?.name !== this.editModel.name) {
                const promisesTCoud = this.designs.map(async (design) => {
                    if (this.editModel.name && design.drawings?.data[0].uid) {
                        const { documentId } = TCloudService.parseFileUid(design.drawings?.data[0].uid);
                        return await TCloudService.updateDocument(documentId, {
                            name: this.editModel.name,
                        });
                    }
                });
                await Promise.all(promisesTCoud);
            }

            if (this.currentMode.emit) this.$emit(this.currentMode.emit);
            this.$emit('update-designs');
            this.modal.hideModal({
                close: true,
            });
        } catch (error) {
            this.$modal.show(ModalEnum.Error, { error });
        } finally {
            this.updatingInProcess = false;
        }
    }

    public resetEditModel() {
        this.editModel = { generalDesigners: [] };
    }

    // TODO: problem with upload data
    public async initEditModel() {
        this.currentMode.status = DesignStatusEnum.checking;

        if (this.designs?.length) {
            const [design] = this.designs;
            this.currentMode.status = design.status;
            this.editModel = DesigningService.toUpdateModel(design);
        } else this.editModel.name = this.formattedFiles[0]?.name;

        this.editModel.deadline = this.currentMode.defaultDate || this.editModel.deadline;

        await this.currentProjectChangeHandler();
    }

    public beforeCloseHandler(event: IModalEvent) {
        if (!event.params?.close && this.currentMode.mode === DesignUpdateModalMode.OnCreate) {
            event.cancel();

            this.$modal.show(ModalEnum.Confirm, {
                title: this.$t('page.designing.modal.tcloudCloseConfirmTitle'),
                onConfirm: () => {
                    this.modal.hideModal({
                        close: true,
                    });
                },
            });
        }
    }

    public async initModes() {
        this.modes[DesignUpdateModalMode.SendingForRevision].defaultDate = (await getWorkDateAfterDays(5)).toFormat(
            'dd.MM.yyyy',
        );
        this.modes[DesignUpdateModalMode.OnCreate].defaultDate = (await getWorkDateAfterDays(10)).toFormat(
            'dd.MM.yyyy',
        );
        this.modes[DesignUpdateModalMode.BackToWork].defaultDate = (await getWorkDateAfterDays(10)).toFormat(
            'dd.MM.yyyy',
        );
    }

    public async created() {
        this.modes = [
            {
                mode: DesignUpdateModalMode.Default,
                title: this.$t(`page.designing.modal.${this.itemType}SetData`),
                secondaryBtnTitle: this.$t('page.designing.modal.abort'),
                submitBtnTitle: this.$t('page.designing.modal.save'),
                emit: 'updated',
                requireDate: false,
            },
            {
                mode: DesignUpdateModalMode.TaskCreation,
                title: this.$t(`page.designing.modal.${this.itemType}SetDataForTask`),
                secondaryBtnTitle: this.$t('page.designing.modal.abort'),
                submitBtnTitle: this.$t('page.designing.modal.next'),
                emit: 'to-task-creation',
                requireDate: false,
            },
            {
                mode: DesignUpdateModalMode.SendingForRevision,
                title: this.$t('page.designing.modal.pleaseCheckData'),
                description: this.$t(`page.designing.modal.${this.itemType}CheckBeforeRevision`),
                submitBtnTitle: this.$t('send'),
                emit: 'for-revision',
                requireDate: true,
            },
            {
                mode: DesignUpdateModalMode.OnCreate,
                title: this.$t(`page.designing.modal.${this.itemType}SetData`),
                submitBtnTitle: this.$t('page.designing.modal.save'),
                requireDate: true,
            },
            {
                mode: DesignUpdateModalMode.BackToWork,
                title: this.$t('page.designing.modal.pleaseCheckData'),
                description: this.$t(`page.designing.modal.${this.itemType}CheckBeforeBackToWork`),
                submitBtnTitle: this.$t('send'),
                emit: 'to-work',
                requireDate: true,
            },
            {
                mode: DesignUpdateModalMode.BackToWorkFromRequest,
                title: this.$t('page.designing.modal.pleaseCheckData'),
                description: this.$t(`page.designing.modal.${this.itemType}CheckBeforeBackToWork`),
                submitBtnTitle: this.$t('send'),
                emit: 'to-work-from-request',
                requireDate: true,
            },
            {
                mode: DesignUpdateModalMode.BackToRework,
                title: this.$t('page.designing.modal.pleaseCheckData'),
                description: this.$t(`page.designing.modal.${this.itemType}CheckBeforeBackToRework`),
                submitBtnTitle: this.$t('send'),
                emit: 'to-rework',
                requireDate: true,
            },
        ];

        this.stages = this.isModel ? this.allDesignStages.slice(2, 5) : this.allDesignStages;
        this.currentMode = this.modes[DesignUpdateModalMode.Default];
        await this.initModes();
        this.resetEditModel();
        // await this.GET_ALL_PROJECTS();
    }
}
